/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, type ReactNode } from "react";

import { buildScoutDeepLinkUrl, type ScoutDeepLinkTarget } from "./scoutDeepLinks";

/**
 * Conversation-level deep-link state derived from the current route/hash.
 *
 * This provider should live around the staff observe conversation surface only.
 * It owns the URL-level target (`#id=...`) and the base URL used
 * when leaf components build copyable message/tool links. Keep this context
 * limited to route-scoped primitives so streaming message updates do not churn
 * the value.
 */
type ScoutDeepLinkContextValue = {
  target: ScoutDeepLinkTarget | undefined;
  baseUrl: string | undefined;
};

const ScoutDeepLinkContext = createContext<ScoutDeepLinkContextValue | null>(null);

/**
 * Provides the route-level deep-link target and link-building base URL for the
 * staff observe Scout conversation surface.
 */
export function ScoutDeepLinkProvider({
  target,
  baseUrl,
  children,
}: {
  target?: ScoutDeepLinkTarget;
  baseUrl?: string;
  children: ReactNode;
}) {
  const targetId = target?.id;
  const stableTarget = useMemo<ScoutDeepLinkTarget | undefined>(() => {
    return targetId ? { id: targetId } : undefined;
  }, [targetId]);
  const value = useMemo(() => ({ target: stableTarget, baseUrl }), [baseUrl, stableTarget]);

  return <ScoutDeepLinkContext.Provider value={value}>{children}</ScoutDeepLinkContext.Provider>;
}

/** True when this subtree is inside a Scout deep-link provider. */
export function useScoutDeepLinksEnabled(): boolean {
  return useScoutDeepLinkContext() !== null;
}

/** Returns the active deep-link id, when deep linking is enabled. */
export function useScoutDeepLinkTargetId(): string | undefined {
  return useScoutDeepLinkContext()?.target?.id;
}

/** True when the supplied id is the active deep-link target. */
export function useScoutIsDeepLinkTarget(id: string | undefined): boolean {
  const targetId = useScoutDeepLinkTargetId();
  return Boolean(targetId && id === targetId);
}

/** Builds the copyable deep link for the supplied target id. */
export function useScoutDeepLinkUrl(id: string | undefined): string | undefined {
  const context = useScoutDeepLinkContext();
  const baseUrl = context?.baseUrl;
  const deepLinksEnabled = context !== null;

  return useMemo(() => {
    if (!deepLinksEnabled || !id) {
      return undefined;
    }

    return buildScoutDeepLinkUrl({
      target: { id },
      baseUrl,
    });
  }, [baseUrl, deepLinksEnabled, id]);
}

function useScoutDeepLinkContext(): ScoutDeepLinkContextValue | null {
  return useContext(ScoutDeepLinkContext);
}
